import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/Branding/Logo'; // Import the Logo component
import TextInput from '../../components/FormElements/TextInput';
import PasswordInput from '../../components/FormElements/PasswordInput';
import SelectInput from '../../components/FormElements/SelectInput';
import CheckboxInput from '../../components/FormElements/CheckboxInput';

import useCountryOptions from '../../hooks/useCountryOptions';

function RegisterUserScreen() {
    const countryOptions = useCountryOptions();
    const [formData, setFormData] = useState({
        fullName: '', // Changed from firstName, lastName
        country: '',
        email: '',
        birthDate: '',
        password: '',
        confirmPassword: '',
        privacyPolicy: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const handleChange = (e) => {
        setError(null); // Clear error on new input
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState, // For checkbox, use e.target.checked
            [name]: e.target.type === 'checkbox' ? e.target.checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        // Basic validation example
        if (formData.password !== formData.confirmPassword) {
            alert("Las contraseñas no coinciden.");
            return;
        }
        if (!formData.privacyPolicy) {
            alert("Debes aceptar las políticas de privacidad para continuar.");
            return;
        }
        // Since react-select doesn't use native 'required', we check it here.
        if (!formData.country) { // Assuming an empty string means no selection
            alert("Por favor, selecciona tu país de origen.");
            return;
        }

        const payload = {
            username: formData.fullName,
            email: formData.email,
            password: formData.password,
            birth_date: formData.birthDate,
            country: formData.country
        };

        setIsSubmitting(true);
        console.log('Registering user with payload:', payload);

        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

        try {
            const response = await fetch(`${API_BASE_URL}/api/users/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (!response.ok) {
                // Assuming backend sends error in { error: { message: "..." } } or { message: "..." }
                const errorMessage = data.error?.message || data.message || `Registration failed with status: ${response.status}`;
                setError(errorMessage);
                throw new Error(errorMessage);
            }

            console.log('User registration successful:', data);
            alert('¡Registro exitoso! Serás redirigido al inicio de sesión.');
            navigate('/login'); // Navigate to login screen or a confirmation page
        } catch (err) {
            console.error('Registration error:', err.message);
            if (!error) setError(err.message || 'Ocurrió un error durante el registro. Por favor, inténtalo de nuevo.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        // Updated to match register.html: col-md-9 and m-auto for centering
        <div className="col-md-9 form-side m-auto">
            <div className="form-container text-center">
                <Logo /> {/* Using the imported Logo component */}
                <h3 className="fw-bold mb-4">Registro viajero</h3>
                <p className="text-muted mb-4">Por favor, completa el siguiente formulario para continuar con tu registro.</p>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                <form onSubmit={handleSubmit} className="checkin-form text-start" noValidate>
                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Nombre completo"
                                id="fullName"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <SelectInput
                                label="País de origen"
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                defaultOptionText="Selecciona tu país..."
                                options={countryOptions}
                            />
                        </div>
                    </div>

                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Email"
                                type="text"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                iconClass="bi bi-envelope" // Added icon for email
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <TextInput
                                label="Fecha de nacimiento"
                                type="date"
                                id="birthDate"
                                name="birthDate"
                                value={formData.birthDate}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                ariaLabel="Fecha de nacimiento"
                                iconClass="bi bi-calendar3"
                            />
                        </div>
                    </div>

                    <div className="row gx-5">
                        <div className="col-md-6 mb-3">
                            <PasswordInput
                                label="Contraseña"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                ariaLabel="Contraseña"
                                toggleAriaLabel="Mostrar u ocultar contraseña"
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <PasswordInput
                                label="Confirmar contraseña"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                disabled={isSubmitting}
                                required
                                ariaLabel="Campo para confirmar contraseña"
                                toggleAriaLabel="Mostrar u ocultar campo de confirmación de contraseña"
                            />
                        </div>
                    </div>

                    <div className="my-4">
                        <CheckboxInput
                            id="privacyPolicy"
                            name="privacyPolicy"
                            checked={formData.privacyPolicy}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            required
                            label={<>Confirmo que he leído y acepto las <a href="#">políticas de privacidad</a> de Checkin.</>}
                        />
                    </div>

                    <div className="buttons-container d-grid gap-3 mt-5 mb-3">
                        <button type="submit" className="btn btn-checkin" disabled={isSubmitting}>
                            {isSubmitting ? 'Registrando...' : 'Registrarme'}
                        </button>
                    </div>
                </form>
                {/* Link to login can be added here if desired, though not explicitly in register.html form area */}
                {/* <p className="mt-3">¿Ya tienes una cuenta? <Link to="/login">Inicia sesión aquí</Link></p> */}
            </div>
        </div>
    );
}

export default RegisterUserScreen;